// @flow
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  Hidden,
  Typography,
  Paper,
  Chip,
  Divider,
  Card,
  CardContent,
  Box,
  Avatar,
  Step,
  Stepper,
  StepLabel,
  StepContent,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Done as DoneIcon,
  DoneAll as DoneAllIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  Create as SignatureIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Home as AddressIcon,
  Business as BusinessIcon,
  HourglassEmpty as PendingIcon,
  Cancel as RejectedIcon,
  Warning as WarningIcon,
  VerifiedUser as VerifiedIcon,
} from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SignatureCanvas from 'react-signature-canvas';
import SignProcess from '../model/SignProcess';
import { doGet, doGetBinary } from '../util/api';
import { camelCaseToSentence } from '../util/string';
import { AuthContext } from '../context/auth';
import routes from '../routes';
import ApiResponse from '../model/ApiResponse';

const maxSize: number = Number.parseInt(`${process.env.REACT_APP_UPLOAD_LIMIT || '0'}`);
const baseUrl: string = String(process.env.REACT_APP_BASE_URL);
const videoData = 'video-data';
const useStyles = makeStyles((theme) => ({
  draft: {
    color: theme.palette.secondary['900']
  },
  grey: {
    color: theme.palette.text.secondary
  },
  error: {
    color: theme.palette.error.main
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary
  },
  marginTop: {
    marginTop: theme.spacing(2)
  },
  marginVertical: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  upload: {
    display: 'none'
  },
  canvas: {
    width: '100%',
    maxWidth: 300,
    border: 'solid black .5px'
  },
  clearButton: {
    float: 'right'
  },
  // New modern styling
  sectionTitle: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sectionPaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  statusChip: {
    marginLeft: 'auto',
    fontWeight: 500,
  },
  successChip: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    '& .MuiChip-icon': {
      color: theme.palette.success.contrastText,
    },
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  signatureContainer: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  signatureContent: {
    position: 'relative',
    zIndex: 1,
  },
  signatureLogo: {
    fontSize: '2.5rem',
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      '& .MuiTypography-h6': {
        fontSize: '1.1rem',
        marginLeft: '0 !important',
      },
    },
  },
  canvasContainer: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    border: `2px solid ${theme.palette.grey[300]}`,
    position: 'relative',
    '& canvas': {
      width: '100%',
      height: 200,
      borderRadius: theme.shape.borderRadius,
      border: `1px solid ${theme.palette.grey[300]}`,
      [theme.breakpoints.down('sm')]: {
        height: 150,
      },
    }
  },
  validationContainer: {
    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  validationContent: {
    position: 'relative',
    zIndex: 1,
  },
  qrContainer: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    margin: `${theme.spacing(2)}px auto`,
    display: 'inline-block',
    border: `2px solid ${theme.palette.grey[200]}`,
  },
  uploadArea: {
    padding: theme.spacing(2),
    border: `2px dashed ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    textAlign: 'center',
    backgroundColor: theme.palette.grey[50],
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.5),
    },
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[100],
    },
  },
  userInfoCard: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    marginBottom: theme.spacing(2),
  },
  securityBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.shape.borderRadius,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    marginTop: theme.spacing(2),
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  choiceContainer: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    color: 'white',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  choiceButtons: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    '& .MuiToggleButtonGroup-root': {
      width: '100%',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: theme.spacing(1),
      },
    },
    '& .MuiToggleButton-root': {
      flex: 1,
      flexDirection: 'column',
      padding: theme.spacing(2),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      border: '2px solid rgba(255, 255, 255, 0.3)',
      color: 'white',
      minHeight: 120,
      [theme.breakpoints.down('sm')]: {
        minHeight: 80,
        flexDirection: 'row',
        justifyContent: 'flex-start',
        textAlign: 'left',
        padding: theme.spacing(1.5),
        '& svg': {
          fontSize: '1.5rem !important',
          marginBottom: '0 !important',
          marginRight: theme.spacing(1),
        },
        '& .MuiTypography-subtitle1': {
          fontSize: '0.9rem',
        },
        '& .MuiTypography-caption': {
          fontSize: '0.75rem',
        },
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&.Mui-selected': {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        color: theme.palette.primary.main,
        border: '2px solid white',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
        },
      },
    },
  },
  optionCard: {
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    border: `2px solid ${theme.palette.grey[200]}`,
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
    '&.selected': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
  optionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  },
  optionAvatar: {
    backgroundColor: theme.palette.primary.main,
    width: 56,
    height: 56,
    [theme.breakpoints.down('sm')]: {
      width: 40,
      height: 40,
    },
  },
  optionContent: {
    paddingLeft: theme.spacing(9),
    [theme.breakpoints.down('sm')]: {
      paddingLeft: 0,
    },
  },
  stepperContainer: {
    backgroundColor: 'transparent',
    '& .MuiStepLabel-label': {
      fontWeight: 600,
    },
    '& .MuiStepIcon-root': {
      fontSize: '1.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      '& .MuiStepContent-root': {
        paddingLeft: theme.spacing(2),
        borderLeft: `1px solid ${theme.palette.grey[300]}`,
      },
      '& .MuiStepLabel-label': {
        fontSize: '0.9rem',
      },
    },
  },
  validatedContainer: {
    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.1"%3E%3Ccircle cx="20" cy="20" r="4"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.2,
    },
  },
  validatedContent: {
    position: 'relative',
    zIndex: 1,
  },
  rejectedCard: {
    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  contractPendingCard: {
    background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  contractApprovedCard: {
    background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
    color: 'white',
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-11.046-8.954-20-20-20v20h20z"/%3E%3C/g%3E%3C/svg%3E") repeat',
      opacity: 0.1,
    },
  },
  verificationContent: {
    position: 'relative',
    zIndex: 1,
    textAlign: 'center',
  },
  verificationIcon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
    animation: (status) => status === 'verifying' ? '$pulse 2s infinite' : 'none',
  },
  verificationTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  verificationDescription: {
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  verificationBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    borderRadius: theme.shape.borderRadius * 2,
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  fadeInAnimation: {
    animation: '$fadeInUp 0.6s ease-out',
  },
}));

type Props = {
  code: string,
  isRetailClient: boolean,
  isValidation: boolean,
  process: SignProcess,
  expanded: boolean,
  disabled: boolean,
  processing: boolean,
  onExpanded: (string) => (event: any, isExpanded: boolean) => void,
  onSave: (AgreementProps) => Promise<boolean>,
  contractAgreementUrl: string,
  templateAgreementUrl: string,
  validationUrl: string,
  qrValidationUrl: string,
  canLoad: boolean,
};

const ProcessAgreementData = (props: Props) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const {
    code, isRetailClient, isValidation, process, expanded, disabled: disabledProp,
    onExpanded, onSave, processing, contractAgreementUrl, templateAgreementUrl,
    validationUrl, canLoad
  } = props;

  const { contractAgreementStatus } = process;
  const disabled = disabledProp || process.hasContractAgreementApproved || contractAgreementStatus === 'approved';

  const [downloading, setDownloading] = useState<boolean>(false);
  const [srcFile, setSrcFile] = useState<string | null>(null);

  const [termsChecked, setTermsChecked] = useState<boolean>(false);
  const [file, setFile] = useState<any>();
  const [canvas, setCanvas] = useState<any>();
  const [clientUrl, setClientUrl] = useState<?string>();
  const [signingMethod, setSigningMethod] = useState<'upload' | 'digital'>('digital');
  const [isEditingSignature, setIsEditingSignature] = useState<boolean>(false);
  const [showTermsConfirmation, setShowTermsConfirmation] = useState<boolean>(false);

  const download = useCallback((url: string, open: boolean = true) => {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.open(url, '_blank');
    } else {
      setDownloading(true);
      doGetBinary(url)
        .then((response: ApiResponse) => {
          if (response.ok) {
            if (open) {
              window.open(response.blobLink('application/pdf'), '_blank');
            }
            setSrcFile(response.blobLink('application/pdf'));
          } else if (!response.is401Error()) {
            const errorMessage = response.message || 'error.unknown';
            enqueueSnackbar(t(errorMessage, errorMessage), {
              variant: 'error'
            });
          }
          setDownloading(false);
        });
    }
  }, [enqueueSnackbar, t]);

  useEffect(() => {
    async function fetchData() {
      let updated = false;
      if (!!process && !disabled && termsChecked && !validationUrl && !contractAgreementUrl) {
        await onSave();
        updated = true;
      }

      // Download signed agreement if available, otherwise download template
      if (!!contractAgreementUrl) {
        setSrcFile(contractAgreementUrl);
        setTermsChecked(true); // Auto-accept terms if already signed
      } else {
        download(templateAgreementUrl, false);
      }
      if (updated) {
        window.location.reload(); // Force reload to update status
      }
    }
    fetchData();
  }, [termsChecked, templateAgreementUrl, contractAgreementUrl, process, download, onSave, validationUrl, disabled, canLoad]);

  useEffect(() => {
    if (isEditingSignature) {
      onExpanded(videoData);
    }
  }, [isEditingSignature, onExpanded]);

  let processStatusText = !!contractAgreementUrl ? 'Completed' : 'Not completed';
  let processStatusIcon = !!contractAgreementUrl ? <DoneIcon /> : <EditIcon />;
  let statusColor = !!contractAgreementUrl ? 'primary' : 'default';

  if (process.hasDataValidated()) {
    processStatusText = 'Validated';
    processStatusIcon = <DoneAllIcon />;
    statusColor = 'success';
  }

  const clientPage = () => {
    setDownloading(true);
    doGet(`/customer/sign-process/${process.code}/retail-client-code`)
      .then((response: ApiResponse) => {
        if (response.ok) {
          setClientUrl(`${baseUrl}${routes.contract}/${process.code}/retail/${String(response.data)}`);
        } else if (!response.is401Error()) {
          const errorMessage = response.message || 'error.unknown';
          enqueueSnackbar(t(errorMessage, errorMessage), {
            variant: 'error'
          });
        }
        setDownloading(false);
      });
  };

  const content = () => {

    const termField = (
      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox
            disabled={disabled}
            checked={termsChecked}
            onChange={(e) => {
              if (e.target.checked) {
                setShowTermsConfirmation(true);
              } else {
                setTermsChecked(false);
              }
            }}
            name='terms'
            color='primary' />}
          label={
            t('Accept terms and conditions')}
         />
         {false && (
          <FormControlLabel
          control={<Checkbox
            checked={isEditingSignature}
            onChange={(e) => {
              setIsEditingSignature(e.target.checked);
            }}
            name='terms'
            color='primary' />}
          label={
            t('sign_document')}
         />
         )}

      </Grid>
    );

    const templateField = (
      <Grid item xs={12}>
        <Paper className={classes.sectionPaper} elevation={0}>
          {downloading || !srcFile ? (
            <CircularProgress />
          ) : (
            <div>
              <iframe
                title={!!contractAgreementUrl ? 'Signed Agreement' : 'Agreement Template'}
                src={`${srcFile}#toolbar=0&navpanes=0`}
                style={{ width: '100%', height: '500px', border: 'none' }}
              />
              <div style={{ marginTop: 8, textAlign: 'center', display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                {contractAgreementStatus !== 'pending' && (
                <Button
                  size='medium'
                  variant='contained'
                  color='secondary'
                  startIcon={<ViewIcon />}
                  onClick={() => download(templateAgreementUrl)}
                  disabled={downloading}
                  style={{ fontWeight: 'bold' }}
                >
                  {downloading ? t('Downloading...') : t('Download PDF')}
                </Button>
                )}
                {!!contractAgreementUrl && (
                <Button
                  size='small'
                  variant='outlined'
                  color='primary'
                  startIcon={<ViewIcon />}
                  onClick={() => download(contractAgreementUrl)}
                >
                  {!!contractAgreementUrl ? t('Download Signed Agreement') : t('Download PDF')}
                </Button>
                )}

              </div>
            </div>
          )}
        </Paper>
      </Grid>
    );

    if (process.isNaturalProcessType()) {
      return (<div className={classes.marginVertical}>
        {templateField}

        <Paper className={classes.sectionPaper} elevation={0}>
          <Typography variant="body1">
            {t('Agreement acknowledge')}
          </Typography>
          {termField}


      {!!contractAgreementUrl && !isEditingSignature  && contractAgreementStatus !== 'pending' && (
        <Paper className={classes.sectionPaper} elevation={0}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleIcon color="primary" />
              <Typography variant="body1" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                {t('Agreement successfully signed')}
              </Typography>
            </div>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setIsEditingSignature(true)}
              style={{ fontWeight: 'bold' }}
            >
              {t('Update Signature')}
            </Button>
          </div>
        </Paper>
      )}
        </Paper>

      </div>);
    }

    return (<div className={classes.marginVertical}>
      <Typography variant="h6" className={classes.sectionTitle}>
        <BusinessIcon /> {t('Legal Entity Information')}
      </Typography>

      <Paper className={classes.sectionPaper} elevation={0}>
        <Typography variant="body1" gutterBottom>
          {t('Legal agreement intro', process)}
        </Typography>
      </Paper>

      <Card className={classes.userInfoCard}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            <BusinessIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {t('Company Information')}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" style={{ opacity: 0.8 }}>
                {t('Company name').toUpperCase()}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {!!process.companyData ? process.companyData.name : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" style={{ opacity: 0.8 }}>
                {'R.U.C.'.toUpperCase()}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {!!process.companyData ? process.companyData.legalID : ''}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" style={{ opacity: 0.8 }}>
                {t('Legal representative full name').toUpperCase()}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {process.name}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" style={{ opacity: 0.8 }}>
                {t('Legal ID').toUpperCase()}
              </Typography>
              <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                {process.legalID}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table view for roles */}
      <Paper className={classes.sectionPaper} elevation={0}>
        <Typography variant="subtitle2" gutterBottom color="textSecondary">
          {t('Signing Authority')}
        </Typography>
        <Grid container spacing={1} className={classes.marginTop}>
          <Grid item xs={12} sm={12} md={2} lg={1}>
            <Typography variant="subtitle2"><strong>#</strong></Typography>
          </Grid>
          <Hidden mdUp>
            <Grid item xs={12} sm={12} md={2} lg={1}>
              <Typography variant="body2">1</Typography>
            </Grid>
          </Hidden>
          <Grid item xs={12} sm={12} md={4} lg={4}>
            <Typography variant="subtitle2"><strong>{t('Name of people who will use a sign')}</strong></Typography>
          </Grid>
          <Hidden mdUp>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <Typography variant="body2">{process.name}</Typography>
            </Grid>
          </Hidden>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            <Typography variant="subtitle2"><strong>{t('Legal ID')}</strong></Typography>
          </Grid>
          <Hidden mdUp>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Typography variant="body2">{process.legalID}</Typography>
            </Grid>
          </Hidden>
          <Grid item xs={12} sm={12} md={3} lg={3}>
            <Typography variant="subtitle2"><strong>{t('Role')}</strong></Typography>
          </Grid>
          <Hidden mdUp>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Typography variant="body2">{!!process.companyData ? process.companyData.customerCompanyRol : ''}</Typography>
            </Grid>
          </Hidden>
        </Grid>
        <Hidden smDown>
          <Grid container spacing={1} className={classes.marginVertical}>
            <Grid item xs={12} sm={12} md={2} lg={1}>
              <Typography variant="body2">1</Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <Typography variant="body2">{process.name}</Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Typography variant="body2">{process.legalID}</Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={3} lg={3}>
              <Typography variant="body2">{!!process.companyData ? process.companyData.customerCompanyRol : ''}</Typography>
            </Grid>
          </Grid>
        </Hidden>
      </Paper>

      {/* Company address details */}
      <Typography variant="h6" className={classes.sectionTitle}>
        <AddressIcon /> {t('Company Address Details')}
      </Typography>

      <Paper className={classes.sectionPaper} elevation={0}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('RUC address').toUpperCase()}
            </Typography>
            <Typography variant="body1">
              {!!process.companyData ? process.companyData.address : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('Province').toUpperCase()}
            </Typography>
            <Typography variant="body1">
              {!!process.companyData ? process.companyData.province : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('City').toUpperCase()}
            </Typography>
            <Typography variant="body1">
              {!!process.companyData ? process.companyData.city : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('Valid company email').toUpperCase()}
            </Typography>
            <Typography variant="body1">
              {process.email}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="textSecondary">
              {t(camelCaseToSentence('phone')).toUpperCase()}
            </Typography>
            <Typography variant="body1">
              {process.phone}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="textSecondary">
              {t('Company phone').toUpperCase()}
            </Typography>
            <Typography variant="body1">
              {!!process.companyData ? process.companyData.phone : ''}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.sectionPaper} elevation={0}>
        <Typography variant="body2" color="textSecondary">
          {t('Legal agreement note')}
        </Typography>
      </Paper>

      {templateField}

      <Paper className={classes.sectionPaper} elevation={0}>
        <Typography variant="body1">
          {t('Agreement acknowledge')}
        </Typography>
      </Paper>

      {termField}

      {!!contractAgreementUrl && !isEditingSignature && (
        <Paper className={classes.sectionPaper} elevation={0}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircleIcon color="primary" />
              <Typography variant="body1" style={{ fontWeight: 'bold', color: '#4caf50' }}>
                {t('Agreement successfully signed')}
              </Typography>
            </div>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setIsEditingSignature(true)}
              style={{ fontWeight: 'bold' }}
            >
              {t('Update Signature')}
            </Button>
          </div>
        </Paper>
      )}

      {!isValidation && <Paper className={classes.sectionPaper} elevation={0}>
        <Typography variant="body1">
          {t('Agreement farewell')}
        </Typography>
      </Paper>}
    </div>);
  };

  // Function to render contract agreement status feedback
  const renderContractAgreementStatus = () => {
    if (!contractAgreementStatus || contractAgreementStatus === 'none') {
      return null;
    }

    switch (contractAgreementStatus) {
      case 'pending':
        return (
          <Grid item xs={12} className={classes.fadeInAnimation}>
            <div className={classes.contractPendingCard}>
              <div className={classes.verificationContent}>
                <AssignmentIcon className={classes.verificationIcon} />
                <Typography variant="h5" className={classes.verificationTitle}>
                  {t('Contract Agreement Pending')}
                </Typography>
                <Typography variant="body1" className={classes.verificationDescription}>
                  {t('Your contract agreement is currently under review')}
                </Typography>
                <div className={classes.verificationBadge}>
                  <PendingIcon fontSize="small" />
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {t('Pending Review')}
                  </Typography>
                </div>
              </div>
            </div>
          </Grid>
        );

      case 'rejected':
        return (
          <Grid item xs={12} className={classes.fadeInAnimation}>
            <div className={classes.rejectedCard}>
              <div className={classes.verificationContent}>
                <RejectedIcon className={classes.verificationIcon} />
                <Typography variant="h5" className={classes.verificationTitle}>
                  {t('Contract Agreement Rejected')}
                </Typography>
                <Typography variant="body1" className={classes.verificationDescription}>
                  {t('Your contract agreement has been rejected')}
                </Typography>
                <div className={classes.verificationBadge}>
                  <WarningIcon fontSize="small" />
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {t('Rejected')}
                  </Typography>
                </div>
              </div>
            </div>
          </Grid>
        );

      case 'approved':
        return (
          <Grid item xs={12} className={classes.fadeInAnimation}>
            <div className={classes.contractApprovedCard}>
              <div className={classes.verificationContent}>
                <VerifiedIcon className={classes.verificationIcon} />
                <Typography variant="h5" className={classes.verificationTitle}>
                  {t('Contract Agreement Approved')}
                </Typography>
                <Typography variant="body1" className={classes.verificationDescription}>
                  {t('Your contract agreement has been successfully approved and processed')}
                </Typography>
                <div className={classes.verificationBadge}>
                  <CheckCircleIcon fontSize="small" />
                  <Typography variant="body2" style={{ fontWeight: 500 }}>
                    {t('Approved')}
                  </Typography>
                </div>
              </div>
            </div>
          </Grid>
        );

      default:
        return null;
    }
  };

  return (
    <Accordion
      key={code}
      expanded={expanded}
      onChange={onExpanded(code)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${code}-panel`}
        id={`${code}-panel`}
        className={classes.accordionSummary}
      >
        <Typography className={classes.heading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <AssignmentIcon /> {t('Contract agreement')}
        </Typography>
        <Chip
          icon={processStatusIcon}
          label={t(processStatusText)}
          color={statusColor === 'success' ? 'default' : statusColor}
          size="small"
          className={`${classes.statusChip} ${statusColor === 'success' ? classes.successChip : ''}`}
        />
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Contract Agreement Status Feedback */}
          {renderContractAgreementStatus()}

          {processing && <Grid item xs={12}>
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CircularProgress color="primary" style={{ marginBottom: 16 }} />
              <Typography variant="body2" color="textSecondary">
                {t('Processing agreement...')}
              </Typography>
            </div>
          </Grid>}

          {(!!user && user.isRetail && !disabled && !isRetailClient) && (
            <Grid item xs={12}>
              <Paper className={classes.sectionPaper} elevation={0}>
                <Button
                  size='medium'
                  variant='contained'
                  color='primary'
                  startIcon={(processing || downloading) ? <CircularProgress size={20} /> : <PersonIcon />}
                  disabled={processing || downloading}
                  onClick={clientPage}
                  style={{ fontWeight: 'bold' }}>
                  {t('Agreement client page')}
                </Button>
              </Paper>
            </Grid>
          )}

          {/* Only show agreement content if contract is not rejected */}
          {!disabled && (
            <Grid item xs={12}>
              {content()}

              {/* Only show signature options if not approved */}
              {(termsChecked && (isEditingSignature) && contractAgreementStatus !== 'approved') && (
                <Grid item xs={12}>
                  <Divider style={{ margin: '16px 0' }} />

                  {isEditingSignature && (
                    <Paper className={classes.sectionPaper} elevation={0} style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }}>
                      <Typography variant="body2" style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#856404' }}>
                        <EditIcon fontSize="small" />
                        {t('You are updating your existing signature')}
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        color="secondary"
                        onClick={() => setIsEditingSignature(false)}
                        style={{ marginTop: 8 }}
                      >
                        {t('Cancel')}
                      </Button>
                    </Paper>
                  )}

                  {/* Signing Method Selection */}
                  <div className={classes.choiceContainer}>
                    <Typography
                      variant="h5"
                      style={{
                        fontWeight: 'bold',
                        marginBottom: 8,
                        fontSize: window.innerWidth < 600 ? '1.25rem' : '1.5rem'
                      }}
                    >
                      <AssignmentIcon style={{
                        verticalAlign: 'middle',
                        marginRight: 8,
                        fontSize: window.innerWidth < 600 ? '1.5rem' : '2rem'
                      }} />
                      {t('Choose Your Signing Method')}
                    </Typography>

                    <Typography
                      variant="body1"
                      style={{
                        marginBottom: 16,
                        opacity: 0.9,
                        fontSize: window.innerWidth < 600 ? '0.9rem' : '1rem'
                      }}
                    >
                      {t('Select how you would like to complete your agreement signature')}
                    </Typography>

                    <div className={classes.choiceButtons}>
                      <ToggleButtonGroup
                        value={signingMethod}
                        exclusive
                        onChange={(event, newMethod) => {
                          if (newMethod !== null) {
                            setSigningMethod(newMethod);
                          }
                        }}
                        aria-label="signing method"
                        orientation={window.innerWidth < 600 ? "vertical" : "horizontal"}
                      >
                        <ToggleButton value="upload" aria-label="upload signed contract">
                          <UploadIcon style={{ fontSize: '2rem', marginBottom: 8 }} />
                          <div style={{ textAlign: window.innerWidth < 600 ? 'left' : 'center' }}>
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                              {t('Upload Signed Contract')}
                            </Typography>
                            <Typography variant="caption" style={{ opacity: 0.8 }}>
                              {t('Already have a signed PDF')}
                            </Typography>
                          </div>
                        </ToggleButton>

                        <ToggleButton value="digital" aria-label="digital signature">
                          <SignatureIcon style={{ fontSize: '2rem', marginBottom: 8 }} />
                          <div style={{ textAlign: window.innerWidth < 600 ? 'left' : 'center' }}>
                            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                              {t('Digital Signature')}
                            </Typography>
                            <Typography variant="caption" style={{ opacity: 0.8 }}>
                              {t('Sign electronically now')}
                            </Typography>
                          </div>
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                  </div>

                  {/* Upload Option */}
                  {(signingMethod === 'upload') && (
                    <Paper className={`${classes.optionCard} selected`} elevation={2}>
                      <div className={classes.optionHeader}>
                        <Avatar className={classes.optionAvatar}>
                          <UploadIcon style={{ fontSize: '1.5rem' }} />
                        </Avatar>
                        <div style={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            style={{
                              fontWeight: 'bold',
                              color: '#333',
                              fontSize: window.innerWidth < 600 ? '1.1rem' : '1.25rem'
                            }}
                          >
                            {t('Upload Your Signed Contract')}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontSize: window.innerWidth < 600 ? '0.8rem' : '0.875rem' }}
                          >
                            {t('Upload a PDF file with your signature already applied')}
                          </Typography>
                        </div>
                      </div>

                      <div className={classes.optionContent}>
                        <Stepper orientation="vertical" className={classes.stepperContainer}>
                          <Step active={true}>
                            <StepLabel>
                              <Typography variant="subtitle2">
                                {t('Download the template agreement')}
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                {t('Print and sign the downloaded template, then scan or photograph it as a PDF')}
                              </Typography>
                            </StepContent>
                          </Step>

                          <Step active={true}>
                            <StepLabel>
                              <Typography variant="subtitle2">
                                {t('Upload your signed document')}
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Paper className={classes.uploadArea}>
                                <input
                                  accept='.pdf'
                                  id='upload-agreement'
                                  className={classes.upload}
                                  type='file'
                                  onChange={(e) => {
                                    if (e.target.files && e.target.files.length) {
                                      const file = e.target.files[0];
                                      if (file.size >= maxSize) {
                                        enqueueSnackbar(t('Upload limit is MB_SIZE', { size: (maxSize / (1024 * 1024)).toFixed(0) }), {
                                          variant: 'error'
                                        });
                                      } else {
                                        setFile(file);
                                      }
                                    }
                                  }}
                                />
                                <label htmlFor='upload-agreement' style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
                                  {!file ? (
                                    <>
                                      <UploadIcon color="action" style={{ fontSize: 48, marginBottom: 8 }} />
                                      <Typography variant="subtitle2" gutterBottom>
                                        {t('Click to select your signed PDF')}
                                      </Typography>
                                      <Typography variant="caption" color="textSecondary">
                                        {t('Maximum file size')}: {(maxSize / (1024 * 1024)).toFixed(0)}MB
                                      </Typography>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircleIcon color="primary" style={{ fontSize: 48, marginBottom: 8 }} />
                                      <Typography variant="subtitle2" gutterBottom>
                                        {t('File selected successfully')}
                                      </Typography>
                                      <Typography variant="body2" color="textSecondary" gutterBottom style={{ fontWeight: 'bold' }}>
                                        {file.name}
                                      </Typography>
                                      <div className={classes.responsiveButtonGroup} style={{
                                        display: 'flex',
                                        gap: window.innerWidth < 600 ? 8 : 16,
                                        justifyContent: 'center',
                                        flexWrap: 'wrap'
                                      }}>
                                        <Button
                                          disabled={disabled || !termsChecked}
                                          variant='contained'
                                          color='primary'
                                          size={window.innerWidth < 600 ? 'medium' : 'large'}
                                          startIcon={processing ? <CircularProgress color='inherit' size='1rem' /> : <UploadIcon />}
                                          onClick={() => {
                                            onSave({ file, content: null, name: file.name });
                                            setIsEditingSignature(false);
                                          }}
                                          style={{ fontWeight: 'bold', margin: '8px' }}>
                                          {isEditingSignature ? t('Update Signed Contract') : t('Upload Signed Contract')}
                                        </Button>
                                        <Button
                                          disabled={disabled}
                                          variant='outlined'
                                          color='secondary'
                                          size={window.innerWidth < 600 ? 'medium' : 'large'}
                                          startIcon={<DeleteIcon />}
                                          onClick={() => {
                                            setFile(null);
                                          }}
                                          style={{ margin: '8px' }}>
                                          {t('Remove File')}
                                        </Button>
                                      </div>
                                    </>
                                  )}
                                </label>
                              </Paper>
                            </StepContent>
                          </Step>
                        </Stepper>
                      </div>
                    </Paper>
                  )}

                  {/* Digital Signature Option */}
                  {signingMethod === 'digital' && (
                    <Paper className={`${classes.optionCard} selected`} elevation={2}>
                      <div className={classes.optionHeader}>
                        <Avatar className={classes.optionAvatar}>
                          <SignatureIcon style={{ fontSize: '1.5rem' }} />
                        </Avatar>
                        <div style={{ flex: 1 }}>
                          <Typography
                            variant="h6"
                            style={{
                              fontWeight: 'bold',
                              color: '#333',
                              fontSize: window.innerWidth < 600 ? '1.1rem' : '1.25rem'
                            }}
                          >
                            {t('Create Digital Signature')}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            style={{ fontSize: window.innerWidth < 600 ? '0.8rem' : '0.875rem' }}
                          >
                            {t('Sign electronically using your mouse, trackpad, or touch screen')}
                          </Typography>
                        </div>
                      </div>

                      <div className={classes.optionContent}>
                        <Stepper orientation="vertical" className={classes.stepperContainer}>
                          <Step active={true}>
                            <StepLabel>
                              <Typography variant="subtitle2">
                                {t('Create your signature')}
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <div className={classes.canvasContainer}>
                                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                                  {t('Use your mouse, trackpad, or finger to sign in the box below')}
                                </Typography>
                                <SignatureCanvas
                                  ref={(ref) => {
                                    setCanvas(ref);
                                  }}
                                  canvasProps={{
                                    style: {
                                      width: '100%',
                                      height: window.innerWidth < 600 ? 150 : 200,
                                      border: `2px dashed #ccc`,
                                      borderRadius: 8,
                                      backgroundColor: '#fafafa'
                                    }
                                  }}
                                  clearOnResize={false} />
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: 16,
                                  flexWrap: window.innerWidth < 600 ? 'wrap' : 'nowrap',
                                  gap: window.innerWidth < 600 ? 8 : 0
                                }}>
                                  <Button
                                    size={window.innerWidth < 600 ? 'medium' : 'large'}
                                    variant='contained'
                                    color='primary'
                                    startIcon={<DoneIcon />}
                                    onClick={() => {
                                      if (canvas.isEmpty()) {
                                        enqueueSnackbar(t('Please create your signature first'), {
                                          variant: 'error'
                                        });
                                      } else {
                                        const sign = canvas.getTrimmedCanvas().toDataURL('image/png');
                                        onSave({ content: sign, name: null, file: null });
                                        setIsEditingSignature(false);
                                      }
                                    }}
                                    style={{
                                      fontWeight: 'bold',
                                      padding: window.innerWidth < 600 ? '10px 16px' : '12px 24px',
                                      width: window.innerWidth < 600 ? '100%' : 'auto',
                                      marginBottom: window.innerWidth < 600 ? 8 : 0
                                    }}>
                                    {isEditingSignature ? t('Update Digital Signature') : t('Save Digital Signature')}
                                  </Button>
                                  <Button
                                    size='medium'
                                    variant='outlined'
                                    color='secondary'
                                    startIcon={<DeleteIcon />}
                                    onClick={() => {
                                      canvas.clear();
                                    }}
                                    style={{
                                      fontWeight: 'bold',
                                      width: window.innerWidth < 600 ? '100%' : 'auto'
                                    }}>
                                    {t('Clear Signature')}
                                  </Button>
                                </div>
                              </div>
                            </StepContent>
                          </Step>

                          <Step active={true}>
                            <StepLabel>
                              <Typography variant="subtitle2">
                                {t('Signature will be applied to')}
                              </Typography>
                            </StepLabel>
                            <StepContent>
                              <Box style={{
                                backgroundColor: '#f5f5f5',
                                padding: window.innerWidth < 600 ? 12 : 16,
                                borderRadius: 8,
                                border: '1px solid #e0e0e0'
                              }}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                  {t('Signatory')}:
                                </Typography>
                                <Typography
                                  variant="subtitle1"
                                  style={{
                                    fontWeight: 'bold',
                                    color: '#333',
                                    fontSize: window.innerWidth < 600 ? '1rem' : '1.125rem'
                                  }}
                                >
                                  {process.name}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {t('This signature will be legally associated with the above name')}
                                </Typography>
                              </Box>
                            </StepContent>
                          </Step>
                        </Stepper>
                      </div>
                    </Paper>
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>

        <Dialog
          open={!!clientUrl}
          onClose={() => setClientUrl(null)}
          aria-labelledby='alert-dialog-title'
          aria-describedby='alert-dialog-description'
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id='alert-dialog-title'>
            {t('Agreement client page')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-description'>
              {t('Agreement client page description')}
              <br />
              <strong style={{ wordBreak: 'break-all' }}>{clientUrl}</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setClientUrl(null)} color='primary' variant="contained">
              {t('OK')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={showTermsConfirmation}
          onClose={() => setShowTermsConfirmation(false)}
          aria-labelledby='terms-confirmation-title'
          aria-describedby='terms-confirmation-description'
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id='terms-confirmation-title'>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <WarningIcon color="primary" />
              {t('Confirmar los términos y condiciones')}
            </div>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id='terms-confirmation-description'>
              {t('Declaro que he leído íntegramente el contrato de prestación de servicios de emisión de firma electrónica, entiendo y acepto sus términos y condiciones. Al marcar esta casilla y hacer clic en el botón “Acepto el contrato”, presto mi consentimiento expreso, conforme a lo dispuesto en la Ley de Comercio Electrónico, Firmas y Mensajes de Datos. Reconozco que esta manifestación de voluntad tiene la misma validez y efectos jurídicos que una firma manuscrita o electrónica calificada.')}
              <br />
              <strong>{t('Autorizo, además, el tratamiento de mis datos personales de acuerdo con la finalidad establecida en este contrato y en la política de privacidad de EQON EC, en cumplimiento de la Ley Orgánica de Protección de Datos Personales.')}</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setShowTermsConfirmation(false)}
              color='secondary'
              variant="outlined"
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => {
                setTermsChecked(true);
                setShowTermsConfirmation(false);
              }}
              color='primary'
              variant="contained"
              startIcon={<CheckCircleIcon />}
            >
              {t('Aceptar / Continuar')}
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

export default ProcessAgreementData;
